import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppState } from 'src/app/reducers';
import { Store } from '@ngrx/store';
import { CumplesInitFilterRequested, MensajeCumpleRequested, CumplesFilterChangeRequested, UsuarioFuncionesRequested, CumplesClearRequested } from '../store/cumples/cumples.actions';
import { CumpleFilterDates, UsuarioCumple, UsuarioCumpleFotoData, UsuarioFuncion } from 'src/common/cumples';
import { Observable } from 'rxjs';
import { cumplesAll, cumplesEnvioReady, cumplesFilterDates, cumplesFilterReady, cumplesReady, cumplesToday, cumplesTotal, cumplesUsuarioFoto, cumplesUsuarioFunciones, cumplesUsuarioFuncionesReady, CUMPLES_DATE_SEP } from '../store/cumples/cumples.selectors';
import { distinctUntilChanged, filter, first, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { collapseAnimation } from 'src/common/collapse.animation';
import { NgbDateStruct, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerComponent } from 'src/app/shared/datepicker/datepicker.component';

@Component({
	selector: 'cumples',
	templateUrl: './cumples.component.html',
	styleUrls: ['./cumples.component.scss'],
	animations: [ collapseAnimation ]
})
export class CumplesComponent implements OnDestroy {

	private readonly MESSAGE_MIN_LENGTH: number = 2;
	private readonly MESSAGE_MAX_LENGTH: number = 1000;
	private readonly DAY_INDEX: number = 0;
	private readonly MONTH_INDEX: number = 1
	private readonly YEAR_INDEX: number = 2;

	private envioReadySubscription: Subscription;
	private dateSelectSubscription: Subscription;
	private toggleSubscription: Subscription;

	@ViewChild( "filterDropdown", { read: NgbDropdown, static: false } )
	filterDropdown: NgbDropdown;

	@ViewChild( DatepickerComponent, { static: false } )
	set datepicker( datepicker: DatepickerComponent ) {
		if( datepicker && !this.dateSelectSubscription )
			this.dateSelectSubscription = datepicker.onDateSelect().pipe(
				tap( () => {
					if( this.filterDropdown )
						this.filterDropdown.close();
				}),
				distinctUntilChanged(),
				tap( date => this.store.dispatch( new CumplesFilterChangeRequested( { date: this.toString( date ) } ) ) )
			).subscribe();
	}

	cumples$: Observable<UsuarioCumple[]>;
	cumplesReady$: Observable<boolean>;
	cumplesTotal$: Observable<number>;
	envioReady$: Observable<boolean>;
	filterDates$: Observable<CumpleFilterDates>;
	filterReady$: Observable<boolean>;
	cumpleFotoFunc$: Observable<( idUsuarioMf: number) => UsuarioCumpleFotoData>
	cumplesToday$: Observable<string>;
	funciones$: Observable<UsuarioFuncion[]>;
	funcionesReady$: Observable<boolean>;
	formIdUsuarioMf: number;		// Id del usuario Mf que posee el formulario desplegado
	messageForm: FormGroup;


	constructor( private store: Store<AppState>, private formBuilder: FormBuilder ) {
		// Dispatch actions
		this.store.dispatch( new CumplesInitFilterRequested() );

		// Observables initialization
		this.cumples$ = this.store.select( cumplesAll );
		this.cumplesReady$ = this.store.select( cumplesReady );
		this.cumplesTotal$ = this.store.select( cumplesTotal );
		this.envioReady$ = this.store.select( cumplesEnvioReady );
		this.cumpleFotoFunc$ = this.store.select( cumplesUsuarioFoto );
		this.filterDates$ = this.store.select( cumplesFilterDates );
		this.filterReady$ = this.store.select( cumplesFilterReady );
		this.cumplesToday$ = this.store.select( cumplesToday );
		this.funciones$ = this.store.select( cumplesUsuarioFunciones );
		this.funcionesReady$ = this.store.select( cumplesUsuarioFuncionesReady );

		// Form initialization
		// Es importante mencionar que existe un único FormGroup y muchos objetos form en la vista del componente asociados
		// a este FormGroup. Esto es sólo para poder hacer funcionar correctamente la animación collapseAnimation.
		// Lo que garantiza el correcto funcionamiento y validación del FormGroup es que existe a lo sumo un formulario
		// disponible para edición, el resto permanece oculto (colapsado).
		this.messageForm = this.formBuilder.group({
			'message': [ '', [ Validators.minLength( this.MESSAGE_MIN_LENGTH ), Validators.maxLength( this.MESSAGE_MAX_LENGTH ), Validators.required ] ]
		});
		this.envioReadySubscription = this.store.select( cumplesEnvioReady ).pipe(
			tap( envioReady => {
				envioReady?	this.messageForm.enable( { emitEvent: false } ): this.messageForm.disable( { emitEvent: false } );
			})
		).subscribe();
	}

	ngOnDestroy(): void {
		this.store.dispatch( new CumplesClearRequested() );

		if( this.envioReadySubscription )
			this.envioReadySubscription.unsubscribe();
		if( this.toggleSubscription )
			this.toggleSubscription.unsubscribe();
		if( this.dateSelectSubscription )
			this.dateSelectSubscription.unsubscribe();
	}

	toggleCollapse( idUsuarioMf: number ): void {
		this.messageForm.reset();
		this.formIdUsuarioMf = this.formIdUsuarioMf == idUsuarioMf? null: idUsuarioMf;
	}

	onSendMessage( recibeIdUsuarioMf: number ): void {
		// Realizo el pedido de envío de mensaje al store
		this.store.dispatch( new MensajeCumpleRequested( { recibeIdUsuarioMf, mensaje: this.messageForm.controls.message.value } ) );
		// Cuando el envío se haya realizado => colapso el form del mensaje
		this.toggleSubscription = this.store.select( cumplesEnvioReady ).pipe(
			filter( envioReady => envioReady ),
			first(),
			tap( () => this.toggleCollapse( recibeIdUsuarioMf ) )
		).subscribe();
	}

	onCalendarSelected() {
		this.filterDropdown.toggle();
	}

	onPersonaInfoSelected( dropdownOpened: boolean, idUsuarioMf: number ) {
		if( dropdownOpened )
			this.store.dispatch( new UsuarioFuncionesRequested( { idUsuarioMf } ) ) ;
	}

	toDateStruct( date: string ): NgbDateStruct {
		let part: string[] = date.split( CUMPLES_DATE_SEP );
		let day: number = parseInt( part[ this.DAY_INDEX ] );
		let month: number = parseInt( part[ this.MONTH_INDEX ] );
		let year: number = parseInt( part[ this.YEAR_INDEX ] );
		return { day, month, year };
	}

/*
 * private methods
 */

	private toString( date: NgbDateStruct ): string {
		return date.day + CUMPLES_DATE_SEP + date.month + CUMPLES_DATE_SEP + date.year;
	}

	private isFilterDropdown( dropdown: NgbDropdown ): boolean {
		let result: boolean = false;
		if( dropdown ) {
			let classes: DOMTokenList = dropdown['_elementRef']['nativeElement']["classList"];
			result = classes && classes.contains( "calendar__container" );
		}
		return result;
	}

}
